import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card';
import { X, Download, Loader2, QrCode } from 'lucide-react';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react'; // NOVO IMPORT
import { Skeleton } from '../ui/Skeleton';

// URL base do frontend (assumindo que está rodando em localhost:3000 ou no domínio de produção)
// Em um ambiente de produção real, isso deveria ser lido de uma variável de ambiente.
// Para este ambiente de desenvolvimento, usaremos a URL base do host atual.
const getFrontendBaseUrl = () => {
    // Tenta obter a URL base do host atual (ex: http://localhost:3000)
    return `${window.location.protocol}//${window.location.host}`;
};

function LocalSessaoQrCodeModal({ isOpen, onClose, localSessao }) {
    const [qrCodeValue, setQrCodeValue] = useState('');
    const [downloading, setDownloading] = useState(false);
    const qrCodeRef = useRef(null); // Ref para o componente QRCodeSVG

    // Extrai as propriedades necessárias do objeto localSessao
    const localSessaoNome = localSessao?.nomeCompleto || 'N/A';
    const localSessaoQrCodeKey = localSessao?.qrCodeKey; // O UUID que o backend espera

    useEffect(() => {
        if (isOpen && localSessaoQrCodeKey) {
            // 1. Constrói a URL pública completa usando o qrCodeKey (UUID)
            const baseUrl = getFrontendBaseUrl();
            // O parâmetro agora é 'localId'
            const value = `${baseUrl}/quick-checkin-public?localId=${localSessaoQrCodeKey}`;
            setQrCodeValue(value);
        } else {
            setQrCodeValue('');
        }
        // Não há mais loading de API, pois a geração é instantânea
    }, [isOpen, localSessaoQrCodeKey]);

    if (!isOpen) {
        return null;
    }

    const handleDownload = () => {
        if (!qrCodeRef.current || !qrCodeValue) {
            toast.error("QR Code não está pronto para download.");
            return;
        }

        setDownloading(true);
        try {
            // Acessa o elemento SVG/Canvas gerado pelo qrcode.react
            const svgElement = qrCodeRef.current.querySelector('svg');
            if (!svgElement) {
                throw new Error("Elemento QR Code não encontrado.");
            }

            // Converte SVG para Data URL
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Define o tamanho do canvas para uma alta resolução (ex: 1024x1024)
                const targetSize = 1024; 
                canvas.width = targetSize;
                canvas.height = targetSize;
                
                // Desenha a imagem SVG no canvas na resolução alvo
                ctx.drawImage(img, 0, 0, targetSize, targetSize);
                
                // Converte Canvas para PNG Blob/Data URL
                const pngFile = canvas.toDataURL('image/png');
                
                const link = document.createElement('a');
                link.href = pngFile;
                link.download = `qrcode_sala_${localSessaoNome.replace(/\s+/g, '_')}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success('QR Code baixado com sucesso!');
                setDownloading(false);
            };
            
            // Define a fonte da imagem como o SVG Data URL
            // O SVG é vetorial, então podemos desenhá-lo em qualquer tamanho no canvas.
            img.src = 'data:image/svg+xml;base64,' + btoa(svgData);

        } catch (err) {
            console.error('Erro ao baixar QR Code:', err);
            toast.error('Erro ao baixar QR Code.');
            setDownloading(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qrcode-title"
        >
            <Card className="w-full max-w-md relative">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"
                    aria-label="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
                <CardHeader className="text-center">
                    <CardTitle id="qrcode-title" className="flex items-center justify-center mb-2"><QrCode className="mr-2 h-5 w-5" /> QR Code da Sala</CardTitle>
                    <CardDescription className="leading-relaxed">
                        QR Code para o local: <span className="font-medium text-foreground">{localSessaoNome}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center p-6">
                    {qrCodeValue ? (
                        <div ref={qrCodeRef} className="p-2 border rounded-md bg-white shadow-sm">
                            {/* Revertendo o tamanho de exibição para 256px */}
                            <QRCodeSVG value={qrCodeValue} size={256} level="H" /> 
                        </div>
                    ) : (
                        <Skeleton className="h-64 w-64 rounded-md" />
                    )}
                    <p className="text-xs text-muted-foreground mt-4 text-center break-all">
                        Valor codificado: <span className="font-mono text-primary">{localSessaoQrCodeKey || 'N/A'}</span>
                    </p>
                </CardContent>
                <CardFooter className="flex flex-col sm:flex-row justify-end gap-2 border-t pt-4">
                    <Button variant="secondary" onClick={onClose} className="w-full sm:flex-1">
                        Fechar
                    </Button>
                    <Button onClick={handleDownload} disabled={!qrCodeValue || downloading} className="w-full sm:flex-1">
                        {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        Baixar QR Code
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}

export default LocalSessaoQrCodeModal;